"use client";

import { trpcApi } from "@lattice/frontend/lib/trpc/client/index";
import { ResponsiveBar } from "@nivo/bar";
import React from "react";
import StockLobbyingTable from "./stock-lobbying-table";
import { useDebounce, useWindowSize } from "@reactuses/core";

function formatValue(value: number) {
  const absValue = Math.abs(Number(value));
  if (absValue >= 1_000_000) {
    return `${Math.round(absValue / 1_000_000)}M`;
  }
  if (absValue >= 1_000) {
    return `${Math.round(absValue / 1_000)}K`;
  }
  return absValue.toString();
}

function getQuarter(date: Date) {
  const month = date.getMonth(); // Month is 0-indexed (0 = January)
  return Math.floor(month / 3) + 1; // Quarter 1 for Jan-Mar, Quarter 2 for Apr-Jun, etc.
}

export default function StockLobbyingChart({
  symbol,
  view,
}: {
  symbol: string;
  view: "table" | "chart";
}) {
  const [data] = trpcApi.stock.get_lobbying.useSuspenseQuery(
    { symbol },
    { refetchOnMount: false },
  );
  const { width: window_width } = useWindowSize();
  const debounced_width = useDebounce(window_width, 1000);

  const chartData = React.useMemo(() => {
    const result = data.reduce<
      Record<
        number,
        { year: string; Q1: number; Q2: number; Q3: number; Q4: number }
      >
    >((acc, item) => {
      const date = item.Date;
      const year = date.getFullYear();
      const quarter = getQuarter(date);
      const value = item.Amount ?? 0;

      // Find or create the entry for the year in the accumulator
      if (!acc[year]) {
        // Initialize year with all quarters set to 0
        acc[year] = { year: year.toString(), Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
      }

      // Accumulate value for the corresponding quarter
      const quarterKeyMap: Record<number, "Q1" | "Q2" | "Q3" | "Q4"> = {
        1: "Q1",
        2: "Q2",
        3: "Q3",
        4: "Q4",
      };
      const key = quarterKeyMap[quarter];
      if (key) {
        acc[year][key] += value;
      }

      return acc;
    }, {});

    // Convert the accumulator object into an array
    return Object.values(result);
  }, [data]);

  const is_small_screen = debounced_width <= 768;

  if (!data.length)
    return (
      <div className="h-[600px] flex items-center justify-center text-muted-foreground">
        No data available
      </div>
    );

  return (
    <div className="w-full grid grid-cols-1 bg-grey-7 rounded-lg p-4 sm:p-6 border border-[#131314]">
      {view === "table" ? (
        <StockLobbyingTable data={data} />
      ) : (
        <div
          style={{
            width: `min(100%, ${70 + chartData.length * 36}px)`,
          }}
          className="mx-auto h-[30vh] md:h-[600px]"
        >
          <ResponsiveBar
            data={(is_small_screen ? chartData.slice(-5) : chartData).filter(
              (d) => d.Q1 || d.Q2 || d.Q3 || d.Q4,
            )}
            keys={["Q1", "Q2", "Q3", "Q4"]}
            indexBy="year"
            layout="vertical"
            margin={{ top: 10, right: 10, bottom: 80, left: 60 }}
            padding={0.35}
            groupMode="stacked"
            animate={false}
            indexScale={{ type: "band", round: true }}
            enableGridY={true}
            enableGridX={false}
            markers={[
              {
                axis: "y",
                value: 0,
                lineStyle: { stroke: "#666", strokeWidth: 2 },
              },
            ]}
            axisBottom={{
              tickRotation: 0,
              tickPadding: 5,
              tickSize: 5,
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 6,
              format: formatValue,
            }}
            labelSkipWidth={999} // Hide in-bar labels
            labelSkipHeight={999}
            // Color function for buy (green) and sell (red)
            colors={(bar) =>
              bar.id === "Q1"
                ? "#6990DF"
                : bar.id === "Q2"
                  ? "#22C55E"
                  : bar.id === "Q3"
                    ? "#DCAC11"
                    : "#CC4415"
            }
            borderRadius={2}
            theme={{
              grid: {
                line: {
                  stroke: "#374151",
                  strokeDasharray: "2,4",
                },
              },
              axis: {
                ticks: {
                  text: {
                    fill: "#9ca3af",
                    fontSize: 10,
                  },
                  line: {
                    stroke: "#4b5563",
                  },
                },
                domain: {
                  line: {
                    stroke: "#4b5563",
                  },
                },
              },
            }}
            tooltip={(props) => {
              const id = String(props.id);
              const value = Number(props.value);
              const indexValue = String(props.indexValue);
              return (
                <div className="bg-gray-900 text-white p-3 rounded-lg shadow-lg text-sm">
                  <div className="font-semibold mb-1">{indexValue}</div>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-sm"
                      style={{
                        backgroundColor:
                          id === "Q1"
                            ? "#6990DF"
                            : id === "Q2"
                              ? "#22C55E"
                              : id === "Q3"
                                ? "#DCAC11"
                                : "#CC4415",
                      }}
                    />
                    <span>
                      {id}:{" "}
                      {Intl.NumberFormat().format(Math.abs(Number(value)))}
                    </span>
                  </div>
                </div>
              );
            }}
          />
        </div>
      )}
    </div>
  );
}
